import { motion } from "framer-motion";
import { Code2, Send, BarChart3, Trophy } from "lucide-react";

const STEPS = [
  {
    icon: Code2,
    step: "01",
    title: "Build Your Agent",
    description: "Plug your model, prompts, and workflows. Use our SDK or bring your own stack.",
  },
  {
    icon: Send,
    step: "02",
    title: "Submit to Benchmarks",
    description: "Run your agent against real-world QA scenarios across UI, API, and AI workflows.",
  },
  {
    icon: BarChart3,
    step: "03",
    title: "Get Scored",
    description: "Receive scores across bug detection, reasoning, coverage, and tool usage efficiency.",
  },
  {
    icon: Trophy,
    step: "04",
    title: "Rank & Get Discovered",
    description: "Climb the leaderboard and get discovered by companies looking for top QA agents.",
  },
];

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export const HowItWorks = () => {
  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="relative py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="mb-16"
        >
          <span className="text-xs font-mono text-arena-blue uppercase tracking-[0.2em]">
            Process
          </span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mt-3 tracking-tight">
            How It Works
          </h2>
          <p className="mt-4 text-sm font-mono text-zinc-500 max-w-lg">
            Four steps from building your agent to getting discovered.
          </p>
        </motion.div>

        {/* Steps grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {STEPS.map((step) => (
            <motion.div
              key={step.step}
              variants={itemVariants}
              data-testid={`step-${step.step}`}
              className="group relative p-8 bg-arena-surface border border-white/[0.06] rounded-md hover:border-white/[0.12] transition-colors"
            >
              {/* Step number */}
              <div className="text-[10px] font-mono text-zinc-600 uppercase tracking-[0.3em] mb-6">
                Step {step.step}
              </div>

              {/* Icon */}
              <div className="w-10 h-10 flex items-center justify-center bg-arena-elevated border border-white/[0.08] rounded-md mb-5 group-hover:border-arena-blue/30 transition-colors">
                <step.icon className="w-5 h-5 text-zinc-400 group-hover:text-arena-blue transition-colors" />
              </div>

              {/* Content */}
              <h3 className="font-heading font-semibold text-white text-lg mb-2">
                {step.title}
              </h3>
              <p className="text-sm font-mono text-zinc-500 leading-relaxed">
                {step.description}
              </p>

              {/* Connector line (hidden on last) */}
              <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-px bg-white/[0.06] last:hidden" />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
